import GIF from "gif.js/dist/gif.js";
import GIFWorker from "./gif.worker.txt";

import Layer from "./Layer";

type TouchCoord = { x: number; y: number };

export default class Scene {
  playing: boolean = false;
  layers: Array<Layer> = [];
  active_layer: number = 0;
  background: string = "#FFF";

  private _id: string;
  private container: HTMLElement;
  private drawing: boolean = false;
  private lastUpdate: DOMHighResTimeStamp;
  private history: Array<[string, string]>;
  private history_offset: number = 0;

  private active_touches: Array<TouchCoord>;
  private debugText: HTMLSpanElement;

  get id() {
    return this._id;
  }

  constructor(container = document.body, saveData = {}) {
    const { _id, layers, ...props } = saveData;

    this.container = container;
    this._id = _id || crypto.getRandomValues(new Uint16Array(4)).join("-");
    this.layers = layers
      ? layers.map((layer) => Layer.restore(this.container, layer)) // loading existing layers
      : [new Layer(this.container)]; // single new layer for new scenes

    Object.assign(this, props);

    this.history = [];

    this.layers[this.active_layer].activate();
    this.layers.forEach((layer) => layer.render());

    this.container.addEventListener("pointerdown", () => {
      if (this.layers[this.active_layer].drawing) {
        this.onlayerupdate && this.onlayerupdate();
      }
    });
    this.container.addEventListener("pointerup", () => {
      if (!this.layers[this.active_layer].drawing) {
        this.onlayerupdate && this.onlayerupdate();
      }
    });
    this.container.addEventListener("mouseup", this.handleMouseUp, true);
    this.container.addEventListener("touchstart", this.handleTouchStart);
    this.container.addEventListener("touchend", this.handleTouchEnd);
    document.body.addEventListener("keydown", this.handleKeyDown);

    // this.container.addEventListener('touchstart', this.handleGestureStart);
    this.container.addEventListener("touchmove", this.handleGestureChange);
    // this.container.addEventListener('touchend', this.handleGestureEnd);

    this.debugText = document.createElement("span");
    this.debugText.style.cssText =
      "font:24px/1.4 monospace;color:red;font-weight:bold;position:absolute;bottom:50px;left:50px;";
    this.debugText.innerText = "HELLO WORLD";
    // this.container.appendChild(this.debugText);

    requestAnimationFrame(this.renderFrame);
  }

  log = (text) => {
    this.debugText.innerText = text;
  };

  handleGestureStart = (e) => {
    if (e.touches.length !== 2) return;
    e.preventDefault();
    this.active_touches = Array.from(e.touches).map(
      ({ clientX: x, clientY: y }) => ({ x, y }),
    );
  };

  handleGestureChange = (e) => {
    if (e.changedTouches.length !== 2) return;
    e.preventDefault();
    const [t1, t2] = e.changedTouches;

    const { clientX: x1, clientY: y1 } = t1;
    const { clientX: x2, clientY: y2 } = t2;

    const dx1 = x1 - this.active_touches[0].x;
    const dx2 = x2 - this.active_touches[1].x;
    const dy1 = y1 - this.active_touches[0].y;
    const dy2 = y2 - this.active_touches[1].y;

    const offset = [0, 0];

    if (Math.sign(dx1) === Math.sign(dx2)) offset[0] = Math.min(dx1, dx2);
    if (Math.sign(dy1) === Math.sign(dy2)) offset[1] = Math.min(dy1, dy2);

    if (offset[0] || offset[1]) {
      this.layers.forEach(
        (layer) =>
          (layer.offset = [
            layer.offset[0] +
              offset[0] * Math.cos(layer.rotation) -
              offset[1] * Math.sin(layer.rotation),
            layer.offset[1] +
              offset[1] * Math.sin(layer.rotation) +
              offset[0] * Math.sin(layer.rotation),
          ]),
      );
    } else if (
      Math.abs(Math.abs(dx1) - Math.abs(dx2)) < 2 &&
      Math.sign(dy1) !== Math.sign(dy2)
    ) {
      this.layers.forEach(
        (layer) =>
          (layer.scale +=
            y1 < y2
              ? y1 + dy1 > y1
                ? -15e-3
                : 15e-3
              : y2 + dy2 > y2
                ? 15e-3
                : -15e-3),
      );
    } else if (
      Math.sign(dx1) !== Math.sign(dx2) &&
      Math.sign(dy1) !== Math.sign(dy2)
    ) {
      const sign = (y1 < y2 && dx1 > 0) || (y2 < y1 && dx2 > 0) ? -1 : 1;

      const ang = 10e-2 * sign;
      this.layers.forEach((layer) => (layer.rotation += ang));
    }

    // const ctx = this.layers[0].el.getContext('2d');
    // ctx.fillStyle = 'red';
    //ctx.clearRect(this.layers[0].el.width/2, 0, 300, 100);
    // ctx.font = '24px serif';
    this.log(`${Math.abs(dx1) - Math.abs(dx2)}`);
    this.active_touches = [
      { x: x1, y: y1 },
      { x: x2, y: y2 },
    ];
    this.layers.forEach((layer) => layer.render());
    // DEBUG Touches
    // this.ctx.beginPath();
    // this.ctx.arc(x1, y1, 50, 0, 2 * Math.PI)
    // this.ctx.fillStyle='yellow';
    // this.ctx.fill();
    // this.ctx.beginPath();
    // this.ctx.arc(x2, y2, 50, 0, 2 * Math.PI)
    // this.ctx.fillStyle='blue';
    // this.ctx.fill();
  };

  handleGestureEnd = (e) => {};

  next() {
    if (this.playing) return;
    this.layers.forEach((layer) => layer.next());
    this.onnextframe && this.onnextframe();
  }

  prev() {
    if (this.playing) return;
    this.layers.forEach((layer) => layer.prev());
    this.onprevframe && this.onprevframe();
  }

  setLayerFPS(fps, layer = this.layers[this.active_layer]) {
    layer.fps = fps;
    this.onlayerupdate && this.onlayerupdate(layer);
  }

  setLayerColor(color, layer = this.layers[this.active_layer]) {
    layer.color = color || layer.color;
    layer.render();
    this.onlayerupdate && this.onlayerupdate(layer);
  }

  setLayerStrokeWidth(width, layer = this.layers[this.active_layer]) {
    layer.width = width || layer.width;
    layer.render();
    this.onlayerupdate && this.onlayerupdate(layer);
  }

  toggleLayerFill(layer = this.layers[this.active_layer]) {
    layer.fill = !layer.fill;
    layer.render();
    this.onlayerupdate && this.onlayerupdate(this.layers[this.active_layer]);
  }

  increaseLayerFPS(layer = this.layers[this.active_layer]) {
    layer.fps++;
    this.onlayerupdate && this.onlayerupdate(this.layers[this.active_layer]);
  }

  decreaseLayerFPS(layer = this.layers[this.active_layer]) {
    if (layer.fps < 2) return;
    layer.fps--;
    this.onlayerupdate && this.onlayerupdate(this.layers[this.active_layer]);
  }

  addFrame() {
    this.layers[this.active_layer].addFrame();
    this.onlayerupdate && this.onlayerupdate(this.layers[this.active_layer]);
  }

  removeFrame() {
    this.layers[this.active_layer].removeFrame();
    this.onlayerupdate && this.onlayerupdate(this.layers[this.active_layer]);
  }

  handleKeyDown = (e) => {
    if (
      (e.metaKey || e.ctrlKey) &&
      (e.which === 89 || e.which === 37 || e.which === 39)
    )
      e.preventDefault();

    // e.preventDefault();
    // e.stopPropagation();
    // e.stopImmediatePropagation();

    switch (e.which) {
      // cmd+z, undo
      case 90:
        e.metaKey && this.undo();
        break;
      // cmd+y, redo
      case 89:
        e.metaKey && this.redo();
        break;
      case 32:
        e.preventDefault();
        this.play();
        break;
      case 39:
        e.metaKey || e.ctrlKey
          ? (this.layers[this.active_layer].rotation += 0.1)
          : this.next();
        break;
      case 38:
        e.metaKey || e.ctrlKey
          ? (this.layers[this.active_layer].scale += 0.1)
          : this.increaseLayerFPS();
        break;
      case 37:
        e.metaKey || e.ctrlKey
          ? (this.layers[this.active_layer].rotation -= 0.1)
          : this.prev();
        break;
      case 40:
        e.metaKey || e.ctrlKey
          ? (this.layers[this.active_layer].scale -= 0.1)
          : this.decreaseLayerFPS();
    }
  };

  handleMouseUp = (e) => {
    if (!this.onlayerupdate) return;
    this.updateHistory();
    this.onlayerupdate(this.layers[this.active_layer]);
  };

  handleTouchStart = (e) => {
    this.last_pos = [
      e.touches[0].clientX,
      e.touches[0].clientY,
      performance.now(),
    ];
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.handleGestureStart(e);
  };

  handleTouchEnd = (e) => {
    const t = performance.now();

    if (t - this.last_pos[2] > 200) {
      this.updateHistory();
      return;
    }
    const dy = e.changedTouches[0].clientY - this.last_pos[1];
    const dx = e.changedTouches[0].clientX - this.last_pos[0];
    // may not have to worry about these anymore, since touches are distinct
    // from mouse/stylus
    if (
      Math.abs(dx) > 0.1 * this.container.offsetWidth ||
      Math.abs(dy) < 0.1 * this.container.offsetHeight
    )
      return;
    this.handleGestureEnd(e);
    this[dy < 0 ? "prev" : "next"]();
  };

  updateHistory() {
    const [lastUpdatedLayerId, lastUpdatedFrameId] =
      this.history[this.history_offset] ?? [];
    // only add to history if we haven't already done it!
    if (lastUpdatedFrameId === this.layers[this.active_layer].currentFrame.id) {
      return;
    }

    this.history.splice(
      this.history_offset,
      this.history_offset < this.history.length
        ? this.history.length - this.history_offset
        : 0,
      [
        this.layers[this.active_layer].id,
        this.layers[this.active_layer].currentFrame.id,
        null,
      ],
    );

    this.history_offset = this.history.length;
  }

  undo() {
    if (this.history_offset <= 0) return;

    this.history_offset = Math.max(0, this.history_offset - 1);
    const [layerId, frameId] = this.history[this.history_offset];

    const layerIndex = this.layers.findIndex((l) => l.id === layerId);
    const frameIndex = this.layers[layerIndex].frames.findIndex(
      (f) => f.id === frameId,
    );
    const frameDelta = frameIndex - this.layers[layerIndex].frame;

    if (this.layers[layerIndex].id !== this.layers[this.active_layer].id)
      this.active_layer = layerIndex;
    // move all layers if frame is navigated to
    if (this.layers[layerIndex].frame !== frameIndex)
      this.layers.forEach((l) => l.goTo(l.frame + frameDelta));
    // The layer action that was undone
    const layerPath = this.layers[layerIndex].undo(frameIndex);
    // add a third value to the `history` tuple, for `redo` action
    this.history[this.history_offset][2] = layerPath;

    this.onlayeractivate &&
      this.onlayeractivate(this.layers[this.active_layer]);
  }

  redo() {
    if (this.history_offset === this.history.length) return;

    const historyItem = this.history[this.history_offset];
    const [layerId, frameId, path] = historyItem;

    this.history_offset = Math.min(
      this.history.length,
      this.history_offset + 1,
    );

    const layerIndex = this.layers.findIndex((l) => l.id === layerId);
    const frameIndex = this.layers[layerIndex].frames.findIndex(
      (f) => f.id === frameId,
    );
    const frameDelta = frameIndex - this.layers[layerIndex].frame;

    if (!path)
      throw new Error(
        `${layerIndex}:${frameIndex}: No path found for redo action!`,
      );

    if (this.layers[layerIndex].id !== this.layers[this.active_layer].id)
      this.active_layer = layerIndex;
    // move all layers if frame is navigated to
    if (this.layers[layerIndex].frame !== frameIndex)
      this.layers.forEach((l) => l.goTo(l.frame + frameDelta));

    this.layers[layerIndex].frames[frameIndex].addPath(path);
    // unset saved path (as its now back in the frame)
    historyItem[2] = null;

    this.onlayeractivate &&
      this.onlayeractivate(this.layers[this.active_layer]);
    this.layers[layerIndex].render();
  }

  play() {
    this.playing = !this.playing;
    this.layers.forEach((layer) => (layer.playing = this.playing));
    this.layers.forEach((layer) => layer.render());
    this.onplay && this.onplay(this.playing);
    if (!this.playing) this.onlayerupdate && this.onlayerupdate();
  }

  renderFrame = (ts) => {
    if (!this.playing) return requestAnimationFrame(this.renderFrame);
    if (!this.lastUpdate) this.lastUpdate = ts;
    if (ts - this.lastUpdate < 1000 / 60)
      return requestAnimationFrame(this.renderFrame);
    this.lastUpdate = ts;
    this.layers.forEach((layer) => layer.nextFrame(ts));
    this.onlayerupdate && this.onlayerupdate();
    requestAnimationFrame(this.renderFrame);
  };

  activateLayer(layer) {
    if (!layer) return;
    const id = this.layers.findIndex((l) => l.id === layer.id);
    this.layers[this.active_layer].activate();
    this.active_layer = id;
    this.layers[this.active_layer].activate();
    this.onlayeractivate &&
      this.onlayeractivate(this.layers[this.active_layer]);
  }

  addLayer() {
    const newLayer = new Layer(this.container);
    this.layers[this.active_layer].activate();
    this.active_layer = this.layers.push(newLayer) - 1;
    this.layers[this.active_layer].activate();
    this.onlayeradd && this.onlayeradd(this.layers[this.active_layer]);
  }

  moveLayer(layer, to) {
    if (!layer) return;
    const id = this.layers.findIndex((l) => l.id === layer.id);
    const activeLayerId = this.layers[this.active_layer].id;
    if (to < id) {
      this.layers = [
        ...this.layers.slice(0, to),
        layer,
        ...this.layers.slice(to, id),
        ...this.layers.slice(id + 1),
      ];
    } else if (to > id) {
      this.layers = [
        ...this.layers.slice(0, id),
        ...this.layers.slice(id + 1, to + 1),
        layer,
        ...this.layers.slice(to + 1),
      ];
    }

    this.active_layer = this.layers.findIndex((l) => l.id === activeLayerId);
    this.activateLayer(this.layers[this.active_layer]);
  }

  duplicateLayer(layer = this.layers[this.active_layer]) {
    this.layers[this.active_layer].activate();
    this.active_layer = this.layers.push(layer.clone()) - 1;
    this.layers[this.active_layer].activate();
    this.onlayerduplicate &&
      this.onlayerduplicate(this.layers[this.active_layer]);
  }

  removeLayer(layer = this.layers[this.active_layer]) {
    if (this.layers.length < 2) return;
    const i = this.layers.findIndex((l) => l.id === layer.id);
    this.layers.splice(i, 1);
    (this.layers[i] || this.layers[i - 1]).activate();
    this.active_layer = this.layers.indexOf(
      this.layers[i] || this.layers[i - 1],
    );
    this.onlayerremove && this.onlayerremove(layer);
    layer.destroy();
  }

  destroy() {
    this.layers.forEach((layer) => layer.destroy());
    this.container.removeEventListener("mouseup", this.handleMouseUp, true);
    this.container.removeEventListener("touchstart", this.handleTouchStart);
    this.container.removeEventListener("touchend", this.handleTouchEnd);
    document.body.removeEventListener("keydown", this.handleKeyDown);
    this.layers = undefined;
    this.container = undefined;
  }

  exportScene() {
    const sceneCanvas = this.layers[
      this.active_layer
    ].el.cloneNode() as HTMLCanvasElement;
    const ctx = sceneCanvas.getContext("2d");
    document.body.appendChild(sceneCanvas);

    var gif = new GIF({
      quality: 10,
      workers: 2,
      background: "#000",
      workerScript: GIFWorker, //'./gif.worker.js',
      height: sceneCanvas.height, // Math.round(sceneCanvas.height / 4),
      width: sceneCanvas.width, // Math.round(sceneCanvas.width / 4)
    });

    const [, longest_layer] = this.layers.reduce(
      ([count, longest], layer, i) => [
        layer.frames.length > count ? layer.frames.length : count,
        layer.frames.length > count ? layer : longest,
      ],
      [0, null],
    );

    const total_frames = longest_layer.frames.length;

    ctx.fillStyle = this.background;
    ctx.fillRect(0, 0, sceneCanvas.width, sceneCanvas.height);

    this.layers.map((layer) => layer.goTo(0)).map((layer) => layer.render(ctx));

    for (
      var i = 1, ts = performance.now();
      i < total_frames;
      i++, ts += longest_layer.frame_rate + 1
    ) {
      ctx.fillStyle = this.background;
      ctx.fillRect(0, 0, sceneCanvas.width, sceneCanvas.height);

      this.layers
        .map((layer) => layer.nextFrame(ts))
        .map((layer) => layer.render(ctx));

      gif.addFrame(ctx, { copy: true, delay: longest_layer.frame_rate });
    }

    gif.on("finished", function (blob) {
      const href = URL.createObjectURL(blob);
      // hack to correct APPLE'S FUCKING LIE (iPad Pro is "desktop ready", lie)
      try {
        document.createEvent("TouchEvent");
        window.location.href = href;
      } catch (e) {
        window.open(href);
      }

      document.body.removeChild(sceneCanvas);
    });

    gif.render();
  }

  save() {
    return {
      _id: this._id,
      background: this.background,
      active_layer: this.active_layer,
      layers: this.layers.map((layer) => layer.toJSON()),
    };
  }
}
